import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Loader2, Search, Sparkles, ArrowRight, ExternalLink, Zap, Filter, X, Copy, Check, Mail, CheckSquare, Square } from "lucide-react";
import EmailComposeButtons from "@/components/EmailComposeButtons";

export default function Marketplace() {
  const [brands, setBrands] = useState([]);
  const [channel, setChannel] = useState(null);
  const [connectHint, setConnectHint] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [applyingBrand, setApplyingBrand] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [pitchResult, setPitchResult] = useState(null);
  const [copied, setCopied] = useState("");
  const [selectedBrands, setSelectedBrands] = useState(new Set()); // NEW: bulk selection
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { summary, results }
  const [sortBy, setSortBy] = useState("fit"); // fit | payout | az

  useEffect(() => {
    (async () => {
      try {
        const [{ data: m }, { data: p }] = await Promise.all([
          api.get("/marketplace/brands"),
          api.get("/projects"),
        ]);
        setBrands(m.brands || []);
        setChannel(m.channel);
        setConnectHint(m.connect_channel_hint);
        setProjects(p || []);
        if (p?.length) setSelectedProject(p[0].project_id);
      } catch (e) {
        toast.error("Could not load marketplace");
      } finally { setLoading(false); }
    })();
  }, []);

  const categories = useMemo(() => {
    const counts = new Map();
    brands.forEach((b) => counts.set(b.category, (counts.get(b.category) || 0) + 1));
    // Sort categories by count desc so the busy ones surface first
    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
    return [["all", brands.length], ...sorted];
  }, [brands]);

  const filtered = useMemo(() => {
    let list = brands;
    if (category !== "all") list = list.filter((b) => b.category === category);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((b) =>
        b.brand.toLowerCase().includes(q) ||
        b.category.toLowerCase().includes(q) ||
        b.audience_fit.some((t) => t.toLowerCase().includes(q))
      );
    }
    const sorters = {
      fit: (a, b) => (b.affinity || 0) - (a.affinity || 0) || b.avg_payout - a.avg_payout,
      payout: (a, b) => b.avg_payout - a.avg_payout,
      az: (a, b) => a.brand.localeCompare(b.brand),
    };
    return [...list].sort(sorters[sortBy] || sorters.fit);
  }, [brands, category, query, sortBy]);

  const anyFilterActive = category !== "all" || query.trim().length > 0 || sortBy !== "fit";
  const clearFilters = () => { setCategory("all"); setQuery(""); setSortBy("fit"); };

  const apply = async (brand) => {
    if (!projects.length) {
      toast.error("Create a project first — sponsors attach to a project.");
      return;
    }
    setApplyingBrand(brand.brand);
    try {
      const { data } = await api.post("/marketplace/apply-with-ai", {
        brand: brand.brand,
        project_id: selectedProject || undefined,
      });
      setPitchResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply");
    } finally { setApplyingBrand(null); }
  };

  const copyText = async (label, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 1800);
    } catch {
      toast.error("Copy failed — select the text and press Cmd/Ctrl+C");
    }
  };

  const closeResult = () => { setPitchResult(null); setCopied(""); };

  // ---------- Bulk pitch mode ----------
  const toggleBrandSelection = (brandName) => {
    setSelectedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brandName)) next.delete(brandName);
      else next.add(brandName);
      return next;
    });
  };
  const clearSelection = () => setSelectedBrands(new Set());

  const runBulkPitch = async () => {
    if (!projects.length) { toast.error("Create a project first."); return; }
    const arr = Array.from(selectedBrands);
    if (arr.length === 0) return;
    if (arr.length > 10) { toast.error("Max 10 brands per bulk run."); return; }
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const { data } = await api.post("/marketplace/apply-with-ai-bulk", {
        brands: arr,
        project_id: selectedProject || undefined,
      });
      setBulkResult(data);
      clearSelection();
      if (data.summary?.done > 0) {
        toast.success(`${data.summary.done} pitch${data.summary.done > 1 ? "es" : ""} drafted`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk pitch failed");
    } finally { setBulkRunning(false); }
  };
  // ------------------------------------

  const verdictColor = (v) => {
    if (v === "strong-fit") return "#34C759";
    if (v === "decent-fit") return "#FFC107";
    if (v === "weak-fit") return "#FF9500";
    return "#FF3B30";
  };

  return (
    <div className="min-h-screen">
      {/* Sub-header */}
      <div className="sticky top-0 z-30 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-[#27272a]">
        <div className="px-4 md:px-8 min-h-16 flex flex-wrap items-center gap-2 md:gap-4 py-3 md:py-0">
          <Handshake size={16} strokeWidth={1.5} className="text-[#FF3B30]" />
          <div className="sd-label">SPONSOR MARKETPLACE · {filtered.length} BRANDS</div>
          {channel && (
            <div className="sd-label text-zinc-500 ml-2 truncate max-w-[220px]">
              MATCHED TO · <span className="text-white">{channel.title}</span>
            </div>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {projects.length > 1 && (
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="sd-input h-9 max-w-[220px]"
                data-testid="marketplace-project-select"
              >
                {projects.map((p) => <option key={p.project_id} value={p.project_id}>Apply to: {p.title}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 py-8">
        {/* Hero */}
        <div className="mb-8">
          <div className="sd-label mb-3"><span className="text-[#FF3B30]">●</span>&nbsp;&nbsp;OUTBOUND, WITHOUT THE COLD START</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight max-w-3xl">
            Brands that <span className="italic text-zinc-400">already sponsor</span> creators like you.
          </h1>
          <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed">
            Each brand has a working partnerships channel, typical deliverables, and a payout range.
            One click adds them to your pipeline, scores the fit against your audience, and drafts your pitch email.
          </p>
        </div>

        {/* YouTube connect hint */}
        {connectHint && (
          <div className="mb-6 border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-4 flex flex-col md:flex-row items-start md:items-center gap-4" data-testid="marketplace-connect-hint">
            <Zap size={18} strokeWidth={1.5} className="text-[#FF3B30] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="sd-label text-[#FF3B30]">TIP</div>
              <div className="text-sm text-zinc-300 mt-1">
                Connect your YouTube channel to sort this list by audience fit — better matches surface first.
              </div>
            </div>
            <Link to="/analytics" className="sd-btn sd-btn-primary h-9 shrink-0" data-testid="marketplace-connect-cta">
              Connect YouTube <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
          </div>
        )}

        {/* Search + Sort + Filters */}
        <div className="mb-6 space-y-3" data-testid="marketplace-filters">
          {/* Row 1 — search + sort + counter */}
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search size={13} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by brand, category, or tag (e.g. Notion, Fintech, Podcasting)…"
                className="sd-input h-11 pl-9 pr-9 w-full"
                data-testid="marketplace-search-input"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1"
                  aria-label="Clear search"
                  data-testid="marketplace-clear-search"
                >
                  <X size={13} strokeWidth={1.5} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <label className="sd-label text-zinc-500 hidden md:inline">SORT</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sd-input h-11 pr-8 min-w-[180px]"
                data-testid="marketplace-sort-select"
              >
                <option value="fit">Best fit (default)</option>
                <option value="payout">Highest payout</option>
                <option value="az">A → Z</option>
              </select>
            </div>
          </div>

          {/* Row 2 — category chips (horizontal scroll on mobile) with counts */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Filter size={13} strokeWidth={1.5} className="text-zinc-500 shrink-0" />
            {categories.map(([c, count]) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`font-mono text-[11px] uppercase tracking-wider px-3 h-8 shrink-0 border transition-colors flex items-center gap-1.5 ${
                  category === c
                    ? "bg-[#FF3B30] text-white border-[#FF3B30]"
                    : "text-zinc-300 border-[#27272a] hover:border-zinc-500 hover:text-white"
                }`}
                data-testid={`marketplace-filter-${c}`}
              >
                <span>{c === "all" ? "All" : c}</span>
                <span className={`font-mono text-[10px] ${category === c ? "text-white/70" : "text-zinc-500"}`}>{count}</span>
              </button>
            ))}
            {anyFilterActive && (
              <button
                onClick={clearFilters}
                className="ml-auto shrink-0 sd-btn sd-btn-ghost h-8 px-3 text-[11px]"
                data-testid="marketplace-clear-all-btn"
                title="Clear all filters"
              >
                <X size={11} strokeWidth={1.5} /> Clear filters
              </button>
            )}
          </div>

          {/* Result summary line */}
          <div className="font-mono text-[11px] text-zinc-500" data-testid="marketplace-result-count">
            {loading ? "Loading…" : (
              <>
                Showing <span className="text-white">{filtered.length}</span> of {brands.length} brands
                {category !== "all" && <> · <span className="text-white">{category}</span></>}
                {query && <> · matching <span className="text-white">"{query}"</span></>}
                {sortBy !== "fit" && <> · sorted by <span className="text-white">{sortBy === "payout" ? "payout" : "A→Z"}</span></>}
              </>
            )}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="py-20 flex items-center justify-center text-zinc-500">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading brands…
          </div>
        ) : filtered.length === 0 ? (
          <div className="sd-card p-10 md:p-14 text-center" data-testid="marketplace-empty-state">
            <Search size={28} strokeWidth={1.2} className="mx-auto text-zinc-500 mb-4" />
            <div className="font-display text-2xl md:text-3xl mb-2">Nothing matches those filters</div>
            <p className="text-sm text-zinc-400 max-w-md mx-auto mb-6">
              We have {brands.length} brands in the marketplace right now. Try a broader search or clear your filters.
            </p>
            <button onClick={clearFilters} className="sd-btn sd-btn-accent h-11 px-5" data-testid="empty-clear-filters-btn">
              <X size={13} strokeWidth={1.5} /> Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((b) => (
              <div
                key={b.brand}
                className={`sd-card p-6 flex flex-col relative overflow-hidden transition-colors ${selectedBrands.has(b.brand) ? "border-[#FF3B30]/60" : ""}`}
                data-testid={`marketplace-brand-${b.brand}`}
              >
                {/* Colored accent bar */}
                <div
                  className="absolute top-0 left-0 h-[3px] w-full"
                  style={{ background: b.logo_color }}
                />
                {/* Bulk-select checkbox (top-right) */}
                <button
                  onClick={() => toggleBrandSelection(b.brand)}
                  className="absolute top-3 right-3 z-10 p-1 text-zinc-400 hover:text-white transition-colors"
                  title={selectedBrands.has(b.brand) ? "Remove from bulk pitch" : "Add to bulk pitch"}
                  data-testid={`marketplace-select-${b.brand}`}
                  aria-label="Select for bulk pitch"
                >
                  {selectedBrands.has(b.brand)
                    ? <CheckSquare size={18} strokeWidth={1.5} className="text-[#FF3B30]" />
                    : <Square size={18} strokeWidth={1.5} />
                  }
                </button>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <div className="sd-label text-zinc-500">{b.category.toUpperCase()}</div>
                    <div className="font-display text-3xl leading-tight mt-1 truncate">{b.brand}</div>
                  </div>
                  {b.affinity > 0 && (
                    <div
                      className="font-mono text-[10px] px-2 py-0.5 border border-[#34C759]/40 text-[#34C759] flex items-center gap-1 shrink-0 uppercase"
                      data-testid={`marketplace-affinity-${b.brand}`}
                    >
                      <Sparkles size={9} strokeWidth={1.5} /> Match
                    </div>
                  )}
                </div>

                <div className="flex items-baseline gap-2 mb-4">
                  <span className="font-mono text-[11px] text-zinc-500">TYPICAL PAYOUT</span>
                  <span className="font-mono text-lg text-[#34C759]">${b.avg_payout.toLocaleString()}</span>
                </div>

                <div className="mb-4">
                  <div className="sd-label mb-2">DELIVERABLES</div>
                  <ul className="text-xs text-zinc-400 space-y-1">
                    {b.typical_deliverables.map((d, i) => (
                      <li key={i} className="truncate">— {d}</li>
                    ))}
                  </ul>
                </div>

                <div className="mb-4 flex flex-wrap gap-1.5">
                  {b.audience_fit.slice(0, 4).map((tag) => (
                    <span key={tag} className="font-mono text-[10px] uppercase text-zinc-500 border border-[#27272a] px-1.5 py-0.5">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-auto flex items-center gap-2">
                  <button
                    onClick={() => apply(b)}
                    disabled={applyingBrand === b.brand}
                    className="sd-btn sd-btn-accent h-10 flex-1"
                    data-testid={`marketplace-apply-${b.brand}`}
                    title="Adds to pipeline + AI scores fit + drafts pitch email"
                  >
                    {applyingBrand === b.brand ? (
                      <>
                        <Loader2 size={13} className="animate-spin" /> Drafting pitch…
                      </>
                    ) : (
                      <><Sparkles size={13} strokeWidth={1.5} /> Apply + Draft Pitch</>
                    )}
                  </button>
                  <a
                    href={b.pitch_url}
                    target="_blank"
                    rel="noreferrer"
                    className="sd-btn sd-btn-ghost h-10 px-3"
                    title="Visit brand partnerships page"
                    data-testid={`marketplace-external-${b.brand}`}
                  >
                    <ExternalLink size={13} strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="py-20 text-center text-sm text-zinc-500" data-testid="marketplace-empty">
            No brands matched your filter. Try clearing search or category.
          </div>
        )}
      </div>

      {/* Apply-with-AI Result Modal */}
      {pitchResult && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start md:items-center justify-center p-2 md:p-6 overflow-y-auto"
          onClick={closeResult}
          data-testid="pitch-result-modal"
        >
          <div
            className="bg-[#0e0e0f] border border-[#27272a] w-full max-w-3xl my-4 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 md:p-8 border-b border-[#27272a] flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="sd-label mb-3 text-[#34C759]">● ADDED TO PIPELINE — READY TO SEND</div>
                <h3 className="font-display text-3xl md:text-4xl leading-tight">
                  {pitchResult.sponsor?.brand}
                  <span className="text-zinc-500 text-lg md:text-xl ml-2 align-baseline">
                    · ${pitchResult.sponsor?.payout?.toLocaleString()}
                  </span>
                </h3>
                <div className="text-xs text-zinc-500 mt-2 font-mono">
                  STAGE: {pitchResult.sponsor?.stage?.toUpperCase()} · SOURCE: MARKETPLACE
                </div>
              </div>
              <button
                onClick={closeResult}
                className="text-zinc-500 hover:text-white shrink-0"
                data-testid="pitch-result-close-btn"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {/* Fit Score */}
            <div className="px-6 md:px-8 py-6 border-b border-[#27272a]" data-testid="pitch-result-fit-section">
              <div className="sd-label mb-4 flex items-center gap-2">
                <Sparkles size={11} strokeWidth={1.5} className="text-[#FF3B30]" />
                AI FIT SCORE
              </div>
              {pitchResult.fit_score ? (
                <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
                  <div
                    className="font-display text-6xl md:text-7xl leading-none tabular-nums"
                    style={{ color: verdictColor(pitchResult.fit_score.verdict) }}
                    data-testid="pitch-result-score"
                  >
                    {pitchResult.fit_score.score}
                    <span className="text-2xl md:text-3xl text-zinc-500">/100</span>
                  </div>
                  <div>
                    <div
                      className="font-mono text-xs uppercase tracking-widest mb-2"
                      style={{ color: verdictColor(pitchResult.fit_score.verdict) }}
                    >
                      ● {pitchResult.fit_score.verdict?.replace("-", " ")}
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                      {pitchResult.fit_score.alignment}
                    </p>
                    {pitchResult.fit_score.integration_format && (
                      <div className="text-xs text-zinc-500 mb-2">
                        <span className="text-zinc-400">Suggested integration: </span>
                        {pitchResult.fit_score.integration_format}
                      </div>
                    )}
                    {pitchResult.fit_score.risks?.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {pitchResult.fit_score.risks.slice(0, 3).map((r, i) => (
                          <li key={i} className="text-xs text-zinc-500 font-mono">— {r}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-500" data-testid="pitch-result-fit-fallback">
                  {pitchResult.fit_score_error}
                </div>
              )}
            </div>

            {/* Pitch Email */}
            <div className="px-6 md:px-8 py-6" data-testid="pitch-result-email-section">
              <div className="flex items-center justify-between mb-4">
                <div className="sd-label flex items-center gap-2">
                  <Mail size={11} strokeWidth={1.5} className="text-[#FF3B30]" />
                  DRAFTED PITCH EMAIL
                </div>
                {pitchResult.pitch_email && (
                  <button
                    onClick={() => copyText("full", `Subject: ${pitchResult.pitch_email.subject}\n\n${pitchResult.pitch_email.body}`)}
                    className="sd-btn sd-btn-ghost h-8 text-xs"
                    data-testid="pitch-copy-full-btn"
                  >
                    {copied === "full" ? (
                      <><Check size={11} strokeWidth={1.5} className="text-[#34C759]" /> COPIED</>
                    ) : (
                      <><Copy size={11} strokeWidth={1.5} /> COPY ALL</>
                    )}
                  </button>
                )}
              </div>

              {pitchResult.pitch_email ? (
                <div className="space-y-4">
                  <div className="border border-[#27272a] p-4 group relative">
                    <div className="sd-label mb-2 text-zinc-500 flex items-center justify-between">
                      SUBJECT
                      <button
                        onClick={() => copyText("subject", pitchResult.pitch_email.subject)}
                        className="text-zinc-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid="pitch-copy-subject-btn"
                      >
                        {copied === "subject" ? <Check size={11} strokeWidth={1.5} className="text-[#34C759]" /> : <Copy size={11} strokeWidth={1.5} />}
                      </button>
                    </div>
                    <div className="text-sm text-white font-mono" data-testid="pitch-email-subject">
                      {pitchResult.pitch_email.subject}
                    </div>
                  </div>

                  <div className="border border-[#27272a] p-4 group relative">
                    <div className="sd-label mb-2 text-zinc-500 flex items-center justify-between">
                      BODY
                      <button
                        onClick={() => copyText("body", pitchResult.pitch_email.body)}
                        className="text-zinc-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid="pitch-copy-body-btn"
                      >
                        {copied === "body" ? <Check size={11} strokeWidth={1.5} className="text-[#34C759]" /> : <Copy size={11} strokeWidth={1.5} />}
                      </button>
                    </div>
                    <pre className="text-sm text-zinc-200 whitespace-pre-wrap font-sans leading-relaxed" data-testid="pitch-email-body">
{pitchResult.pitch_email.body}
                    </pre>
                  </div>

                  {pitchResult.pitch_email.cta && (
                    <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.04] p-4">
                      <div className="sd-label mb-2 text-[#FF3B30]">SUGGESTED CTA</div>
                      <div className="text-sm text-zinc-200 italic">"{pitchResult.pitch_email.cta}"</div>
                    </div>
                  )}

                  {pitchResult.pitch_email.confidence_notes?.length > 0 && (
                    <div className="border-t border-[#27272a] pt-4">
                      <div className="sd-label mb-2 text-zinc-500">TWEAK BEFORE SENDING</div>
                      <ul className="space-y-1">
                        {pitchResult.pitch_email.confidence_notes.map((n, i) => (
                          <li key={i} className="text-xs text-zinc-400 font-mono">— {n}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* One-click open in mail client */}
                  <div className="border-t border-[#27272a] pt-4">
                    <div className="sd-label mb-2 text-zinc-500">SEND VIA</div>
                    <EmailComposeButtons
                      email={pitchResult.pitch_email}
                      testIdPrefix="pitch-compose"
                      size="lg"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-500" data-testid="pitch-result-email-fallback">
                  {pitchResult.pitch_email_error}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 md:p-8 border-t border-[#27272a] flex flex-col md:flex-row gap-3 bg-[#0a0a0a]/60">
              {pitchResult.brand_pitch_url && (
                <a
                  href={pitchResult.brand_pitch_url}
                  target="_blank"
                  rel="noreferrer"
                  className="sd-btn sd-btn-accent h-11 flex-1"
                  data-testid="pitch-send-external-btn"
                >
                  Open {pitchResult.sponsor?.brand} partnerships <ExternalLink size={13} strokeWidth={1.5} />
                </a>
              )}
              <Link
                to="/sponsors"
                className="sd-btn sd-btn-ghost h-11 flex-1"
                data-testid="pitch-view-sponsors-btn"
              >
                View in Sponsors <ArrowRight size={13} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Bulk pitch progress / result modal */}
      {(bulkRunning || bulkResult) && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="bulk-pitch-modal">
          <div className="bg-[#0e0e0f] border border-[#27272a] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 md:p-8 border-b border-[#27272a] flex items-center justify-between">
              <div>
                <div className="sd-label mb-2 text-[#FF3B30]">● BULK PITCH MODE</div>
                <h3 className="font-display text-2xl md:text-3xl">
                  {bulkRunning ? "Drafting your pitches…" : `${bulkResult.summary.done} pitch${bulkResult.summary.done !== 1 ? "es" : ""} ready`}
                </h3>
              </div>
              {!bulkRunning && (
                <button
                  onClick={() => setBulkResult(null)}
                  className="sd-btn sd-btn-ghost h-9 w-9 p-0"
                  data-testid="bulk-pitch-close-btn"
                  aria-label="Close"
                >
                  <X size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>

            <div className="p-6 md:p-8">
              {bulkRunning && (
                <div className="py-6 text-center">
                  <Loader2 size={32} className="animate-spin mx-auto text-[#FF3B30] mb-4" />
                  <div className="font-mono text-sm text-zinc-400">Running Fit Score + AI pitch draft for each brand.</div>
                  <div className="mt-2 text-xs text-zinc-500">This takes ~15s per brand. Do not close.</div>
                </div>
              )}
              {bulkResult && !bulkRunning && (
                <div>
                  <div className="mb-5 grid grid-cols-3 gap-2 text-center">
                    <div className="border border-[#27272a] p-3">
                      <div className="font-mono text-[10px] text-zinc-500 uppercase">Done</div>
                      <div className="font-display text-2xl text-[#34C759]" data-testid="bulk-summary-done">{bulkResult.summary.done}</div>
                    </div>
                    <div className="border border-[#27272a] p-3">
                      <div className="font-mono text-[10px] text-zinc-500 uppercase">Skipped</div>
                      <div className="font-display text-2xl text-zinc-400" data-testid="bulk-summary-skipped">{bulkResult.summary.skipped}</div>
                    </div>
                    <div className="border border-[#27272a] p-3">
                      <div className="font-mono text-[10px] text-zinc-500 uppercase">Errors</div>
                      <div className="font-display text-2xl text-[#FF3B30]" data-testid="bulk-summary-errors">{bulkResult.summary.errors}</div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-5" data-testid="bulk-result-list">
                    {bulkResult.results.map((r) => (
                      <div key={r.brand} className="border border-[#27272a] p-3">
                        <div className="flex items-center gap-3">
                          {r.status === "done" && <Check size={14} strokeWidth={1.5} className="text-[#34C759] shrink-0" />}
                          {r.status === "partial" && <Sparkles size={14} strokeWidth={1.5} className="text-[#FFC107] shrink-0" />}
                          {r.status === "skipped" && <Square size={14} strokeWidth={1.5} className="text-zinc-500 shrink-0" />}
                          {r.status === "error" && <X size={14} strokeWidth={1.5} className="text-[#FF3B30] shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white truncate">{r.brand}</div>
                            <div className="font-mono text-[10px] uppercase text-zinc-500">
                              {r.status === "done" && `${r.fit_score?.score ?? "—"}/100 · pitch drafted`}
                              {r.status === "partial" && (r.fit_error || r.pitch_error || "partial")}
                              {r.status === "skipped" && "already in your pipeline"}
                              {r.status === "error" && (r.error || "failed")}
                            </div>
                          </div>
                        </div>
                        {r.status === "done" && r.pitch_email && (
                          <div className="mt-2 pl-6">
                            <EmailComposeButtons
                              email={r.pitch_email}
                              testIdPrefix={`bulk-compose-${r.brand}`}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col md:flex-row gap-2">
                    <Link
                      to="/sponsors"
                      className="sd-btn sd-btn-accent h-11 flex-1"
                      data-testid="bulk-view-sponsors-btn"
                    >
                      Open Sponsors board <ArrowRight size={13} strokeWidth={1.5} />
                    </Link>
                    <button
                      onClick={() => setBulkResult(null)}
                      className="sd-btn sd-btn-ghost h-11 md:w-40"
                      data-testid="bulk-close-btn"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sticky bulk-action bar */}
      {selectedBrands.size > 0 && !bulkRunning && !bulkResult && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-xl border-t border-[#27272a] sd-rise" data-testid="bulk-action-bar">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
            <div className="sd-label">
              <span className="text-[#FF3B30]">●</span>&nbsp;&nbsp;{selectedBrands.size} SELECTED · BULK PITCH
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={clearSelection}
                className="sd-btn sd-btn-ghost h-10 px-4 text-xs"
                data-testid="bulk-clear-btn"
              >
                Clear
              </button>
              <button
                onClick={runBulkPitch}
                disabled={bulkRunning || selectedBrands.size > 10}
                className="sd-btn sd-btn-accent h-10 px-5"
                data-testid="bulk-run-btn"
              >
                <Sparkles size={13} strokeWidth={1.5} />
                Draft {selectedBrands.size} pitch{selectedBrands.size > 1 ? "es" : ""} <ArrowRight size={13} strokeWidth={1.5} />
              </button>
            </div>
            {selectedBrands.size > 10 && (
              <div className="w-full text-xs text-[#FF3B30] font-mono">
                × Max 10 brands per bulk run. Deselect {selectedBrands.size - 10}.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
